import React, { useState, useRef, useEffect } from 'react';
import { 
  Menu, X, Phone, MapPin, ChevronDown, ArrowRight,
  Trees, Shovel
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isMobileServiceOpen, setIsMobileServiceOpen] = useState(false);
  
  // Use location to close menu on route change automatically
  const location = useLocation();
  const servicesRef = useRef(null);

  // 1. Reset menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
    setActiveDropdown(null);
    setIsMobileServiceOpen(false);
  }, [location]);

  // 2. Lock Body Scroll when Mobile Menu is Open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  // 3. Auto-scroll to Services on Mobile
  useEffect(() => {
    if (isMobileServiceOpen && servicesRef.current) {
      // Small delay to ensure the DOM has updated with the new height
      setTimeout(() => {
        servicesRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' // Centers the menu in the screen
        });
      }, 150);
    }
  }, [isMobileServiceOpen]);

  const slugify = (text) => {
    return text
      .toLowerCase()
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .replace(/[^\w ]+/g, '') 
      .replace(/ +/g, '-');    
  };

  const serviceCategories = [
    {
      title: "Pflege & Bäume",
      icon: <Trees className="w-5 h-5" />,
      items: [
        { name: "Baumfällen" },
        { name: "Gartenpflege" },
        { name: "Heckenschneiden" },
      ]
    },
    {
      title: "Bau & Montage",
      icon: <Shovel className="w-5 h-5" />,
      items: [
        { name: "Holzterrassen" },
        { name: "Pflastersteine" },
        { name: "Zaunbau" },
      ]
    }
  ];

  return (
    <>
      {/* ================= 1. TOP BAR ================= */}
      <div className="bg-slate-900 text-slate-300 py-3 px-4 text-sm hidden md:block border-b border-white/10 relative z-50">
        <div className="max-w-[1500px] mx-auto flex justify-between items-center">
          <div className="flex gap-6">
            <span className="flex items-center gap-2 hover:text-white cursor-pointer transition">
              <Phone size={15} className="text-emerald-500" /> +49 15735547097
            </span>
            <span className="flex items-center gap-2 hover:text-white cursor-pointer transition">
              <MapPin size={15} className="text-emerald-500" /> Ihr Fachbetrieb in der Region
            </span>
          </div>
          <div className="font-medium text-emerald-400 flex items-center gap-2">
            <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
            Termine 
          </div>
        </div>
      </div>

      {/* ================= 2. NAVBAR ================= */}
      <nav 
        className="sticky top-0 z-[100] bg-white shadow-md font-sans" 
        onMouseLeave={() => setActiveDropdown(null)}
      >
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            
            {/* Logo */}
            <div className="flex items-center gap-2 cursor-pointer z-[101]">
              <Link to="/" className="flex items-center gap-2">
               <img src="./Logo.png" alt="Logo" className="w-[150px] h-auto object-contain" />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1">
              <Link to="/" className="px-4 py-2 font-semibold text-slate-600 hover:text-emerald-700 transition-colors rounded-lg hover:bg-slate-50">
                Startseite
              </Link>
              
              <Link to="/About" className="px-4 py-2 font-semibold text-slate-600 hover:text-emerald-700 transition-colors rounded-lg hover:bg-slate-50">
                Über Uns
              </Link>
              
              {/* MEGA MENU TRIGGER (Fixed: No button inside Link) */}
              <div 
                className="group px-4 py-2 h-full flex items-center"
                onMouseEnter={() => setActiveDropdown('services')}
              >   
                <Link 
                  to="/Services"
                  className={`flex items-center gap-1 font-semibold transition-colors rounded-lg ${activeDropdown === 'services' ? 'text-emerald-700' : 'text-slate-600 group-hover:text-emerald-700'}`}
                >
                  Leistungen 
                  <ChevronDown size={16} className={`transition-transform duration-300 ${activeDropdown === 'services' ? 'rotate-180' : ''}`} />
                </Link>
              </div>

              <Link to="/Project" className="px-4 py-2 font-semibold text-slate-600 hover:text-emerald-700 transition-colors rounded-lg hover:bg-slate-50">
                Galerie
              </Link>
              
              <Link to="/Faq" className="px-4 py-2 font-semibold text-slate-600 hover:text-emerald-700 transition-colors rounded-lg hover:bg-slate-50">
                Häufige Fragen
              </Link>
            </div>

            {/* CTA Button (Desktop Only) */}
            <div className="hidden md:block group">
              <Link to='/contact' className="flex items-center gap-2 bg-emerald-700 py-4 hover:bg-emerald-800 text-white px-7 rounded-lg font-bold transition-all shadow-lg hover:shadow-emerald-900/20 transform hover:-translate-y-0.5">
                Angebot anfordern
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            {/* Mobile Toggle Button */}
            <div className="md:hidden z-[101]">
              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)} 
                className="text-slate-700 p-2 hover:bg-slate-100 rounded-lg transition-colors"
                aria-label="Menu"
              >
                {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
          </div>
        </div>

        {/* ================= 3. DESKTOP MEGA MENU CONTENT ================= */}
        <div 
          className={`hidden md:block absolute top-full left-0 w-full bg-white border-t border-slate-100 shadow-2xl transition-all duration-300 origin-top z-50 ${
            activeDropdown === 'services' 
              ? 'opacity-100 visible translate-y-0' 
              : 'opacity-0 invisible -translate-y-4 pointer-events-none'
          }`}
          onMouseEnter={() => setActiveDropdown('services')}
          onMouseLeave={() => setActiveDropdown(null)}
        >
          <div className="max-w-[1200px] mx-auto px-8 py-10">
            <div className="grid grid-cols-2 gap-12">
              {serviceCategories.map((category, idx) => (
                <div key={idx} className="space-y-6">
                  <div className="flex items-center gap-2 text-emerald-700 font-bold text-lg border-b border-slate-100 pb-3">
                    {category.icon}
                    <h3>{category.title}</h3>
                  </div>
                  <ul className="space-y-4">
                    {category.items.map((item, itemIdx) => (
                      <li key={itemIdx}>
                        <Link 
                          to={`/services/${slugify(item.name)}`} 
                          onClick={() => setActiveDropdown(null)}
                          className="group flex items-center gap-2"
                        >
                          <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
                          <div className="font-semibold text-slate-700 group-hover:text-emerald-600 transition-colors">
                            {item.name}
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ================= 4. MOBILE DROPDOWN (FULL SCREEN) ================= */}
        <div 
           className={`md:hidden fixed inset-0 bg-white z-[90] overflow-y-auto transition-transform duration-300 ease-in-out ${
             isMenuOpen ? 'translate-x-0' : 'translate-x-full'
           }`}
           style={{ top: '0px', paddingTop: '80px' }} // Start below header
        >
          <div className="px-4 py-6 flex flex-col min-h-full">
            <div className="space-y-2 flex-grow">
              <Link to="/" onClick={() => setIsMenuOpen(false)} className="block text-lg font-bold text-slate-700 p-4 hover:bg-slate-50 rounded-lg">Startseite</Link>
              
              {/* MOBILE SERVICES SECTION */}
              <div ref={servicesRef} className="scroll-mt-24">
                <button 
                  onClick={() => setIsMobileServiceOpen(!isMobileServiceOpen)}
                  className="w-full flex justify-between items-center text-lg font-bold text-slate-700 p-4 hover:bg-slate-50 rounded-lg"
                >
                  Leistungen
                  <ChevronDown size={20} className={`transition-transform duration-300 ${isMobileServiceOpen ? 'rotate-180 text-emerald-600' : 'text-slate-400'}`} />
                </button>
                
                {/* Collapsible Content */}
                <div 
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isMobileServiceOpen ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="bg-slate-50 rounded-lg mx-2 mb-2 p-2 space-y-4 border border-slate-100">
                    {serviceCategories.map((cat, i) => (
                      <div key={i} className="px-4 py-2">
                        <h4 className="flex items-center gap-2 font-bold text-emerald-700 text-sm uppercase mb-2">
                           {cat.icon} {cat.title}
                        </h4>
                        <ul className="space-y-3 border-l-2 border-slate-200 pl-4">
                          {cat.items.map((item, j) => (
                            <li key={j}>
                              <Link 
                                to={`/services/${slugify(item.name)}`} 
                                onClick={() => setIsMenuOpen(false)}
                                className="block text-slate-600 font-medium py-1 active:text-emerald-700"
                              >
                                {item.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {/* END MOBILE SERVICES SECTION */}

              <Link to="/Project" onClick={() => setIsMenuOpen(false)} className="block text-lg font-bold text-slate-700 p-4 hover:bg-slate-50 rounded-lg">Galerie</Link>
              <Link to="/About" onClick={() => setIsMenuOpen(false)} className="block text-lg font-bold text-slate-700 p-4 hover:bg-slate-50 rounded-lg">Über Uns</Link>
              <Link to="/Faq" onClick={() => setIsMenuOpen(false)} className="block text-lg font-bold text-slate-700 p-4 hover:bg-slate-50 rounded-lg">Häufige Fragen</Link>
            </div>

            <div className="mt-6 pt-6 border-t border-slate-100 pb-10">
              <Link 
                to="/contact" 
                onClick={() => setIsMenuOpen(false)}
                className="w-full bg-emerald-700 text-white py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 hover:bg-emerald-800 transition-colors shadow-lg active:scale-[0.98]"
              >
                Jetzt Angebot anfragen <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Header;